import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material';
import React, { useState } from 'react';
import {
  getInitialSupportRequest,
  supportRequestSchema,
  type ISupportRequest,
} from '../Interfaces/ISupportRequest';
import { useAlert } from '../context/AlertContext';
import { LoadingSpinner } from './LoadingSpinner';
import { useGetUsers } from '../hooks/reactQuerys/UserQueries';
import { usePostSupportRequest } from '../hooks/reactQuerys/SupportQueries';

type IProps = {
  open: boolean;
  onClose: () => void;
};

export const SupportDialogLayout = ({
  children,
  open,
  onClose,
}: IProps & { children: React.ReactNode }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      {children}
    </Dialog>
  );
};

export const SupportDialog = ({ open, onClose }: IProps) => {
  const { showAlert } = useAlert();
  const [sentSuccessfully, setSentSuccessfully] = useState(false);
  const [supportRequest, setSupportRequest] = useState<ISupportRequest>(
    getInitialSupportRequest()
  );
  const [ticketId, setTicketId] = useState<null | number>(null);

  const { users, isLoadingUsers } = useGetUsers();

  const { postSupportRequest, isPendingPostSupportRequest } =
    usePostSupportRequest();

  const onCloseWrapper = () => {
    setSentSuccessfully(false);
    setTicketId(null);
    setSupportRequest(getInitialSupportRequest());
    onClose();
  };

  const handleSendSupportRequest = async () => {
    const parseResult = supportRequestSchema.safeParse(supportRequest);

    if (!parseResult.success) {
      showAlert({
        text: 'Unvollständige Angaben',
        severity: 'error',
      });

      return;
    }

    try {
      const returnObject = await postSupportRequest(parseResult.data);

      if (returnObject.ticket_id) {
        setTicketId(Number(returnObject.ticket_id));
      }

      setSentSuccessfully(true);

      showAlert({
        text: 'Anliegen erfolgreich gesendet',
      });
    } catch {
      showAlert({
        text: 'Fehler beim Senden des Anliegens',
        severity: 'error',
      });
    }
  };

  if (isLoadingUsers) {
    return (
      <SupportDialogLayout open={open} onClose={onCloseWrapper}>
        <LoadingSpinner height='50vh' />
      </SupportDialogLayout>
    );
  }

  if (sentSuccessfully) {
    return (
      <SupportDialogLayout open={open} onClose={onCloseWrapper}>
        <DialogTitle
          mb={2}
          variant='h5'
          sx={{
            borderBottom: '1px solid #ccc',
          }}
        >
          Erstelltes Ticket {ticketId && <> #{ticketId}</>}
        </DialogTitle>
        <DialogContent>
          <Alert severity='success'>
            Ihr Ticket mit der Bezeichnung <i>{supportRequest.Title}</i> wurde
            erfolgreich übermittelt.
            {ticketId && (
              <Box sx={{ mt: 2, fontSize: 20 }}>
                Ihre Ticketnummer lautet #{ticketId}.
              </Box>
            )}
          </Alert>

          <Box sx={{ mt: 2 }}>
            Sie erhalten in den nächsten <u>fünf Minuten</u> eine Bestätigung
            per E-Mail.
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseWrapper} variant='contained'>
            Okay
          </Button>
        </DialogActions>
      </SupportDialogLayout>
    );
  }
  return (
    <>
      <SupportDialogLayout open={open} onClose={onCloseWrapper}>
        <DialogTitle
          mb={2}
          variant='h5'
          sx={{
            borderBottom: '1px solid #ccc',
          }}
        >
          Neues Ticket
        </DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          {sentSuccessfully && (
            <Alert severity='success'>
              Ihr Anliegen wurde erfolgreich übermittelt!
            </Alert>
          )}
          <Grid container spacing={2}>
            <Grid
              item
              sm={12}
              role='menuitem'
              onKeyDown={e => e.stopPropagation()}
            >
              <TextField
                label='Betreff'
                size='small'
                required
                value={supportRequest.Title}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setSupportRequest({
                    ...supportRequest,
                    Title: event.target.value,
                  })
                }
                fullWidth
                sx={{ mt: 1 }}
              />
            </Grid>

            <Grid item sm={12}>
              <TextField
                label='Von'
                size='small'
                value={supportRequest.For_idUser}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setSupportRequest({
                    ...supportRequest,
                    For_idUser: Number(event.target.value),
                  })
                }
                disabled={!users}
                fullWidth
                select
              >
                {users &&
                  users.map(user => (
                    <MenuItem
                      key={`support-idUser-${user.idUser}`}
                      value={user.idUser}
                    >
                      {user.FirstName} {user.LastName}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>

            <Grid item sm={6}>
              <TextField
                label='Priorität'
                size='small'
                value={supportRequest.Priority}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setSupportRequest({
                    ...supportRequest,
                    Priority: event.target.value,
                  })
                }
                fullWidth
                select
              >
                <MenuItem key='support-low' value='support-low'>
                  Niedrig
                </MenuItem>
                <MenuItem key='support-middle' value='support-middle'>
                  Normal
                </MenuItem>
                <MenuItem key='support-high' value='support-high'>
                  Hoch
                </MenuItem>
                <MenuItem key='support-critical' value='support-critical'>
                  Kritisch
                </MenuItem>
              </TextField>
            </Grid>

            <Grid item sm={6}>
              <TextField
                label='Kategorie'
                size='small'
                value={supportRequest.Category}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setSupportRequest({
                    ...supportRequest,
                    Category: event.target.value,
                  })
                }
                fullWidth
                select
              >
                <MenuItem key='support-support' value='support-support'>
                  Hilfe
                </MenuItem>
                <MenuItem key='support-error' value='support-error'>
                  Fehlermeldung
                </MenuItem>
                <MenuItem key='support-wish' value='support-wish'>
                  Vorschlag
                </MenuItem>
                <MenuItem key='support-experience' value='support-experience'>
                  Benutzererfahrung
                </MenuItem>
              </TextField>
            </Grid>

            <Grid
              item
              sm={12}
              role='menuitem'
              onKeyDown={e => e.stopPropagation()}
            >
              <TextField
                label='Beschreibung'
                size='small'
                value={supportRequest.Description}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setSupportRequest({
                    ...supportRequest,
                    Description: event.target.value,
                  })
                }
                fullWidth
                multiline
                rows={5}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: '1px solid #ccc',
          }}
        >
          <Button onClick={onCloseWrapper} variant='outlined'>
            Abbrechen
          </Button>
          <Button
            onClick={handleSendSupportRequest}
            variant='contained'
            disabled={isPendingPostSupportRequest}
          >
            {isPendingPostSupportRequest ? (
              <Box display='flex' alignItems='center' gap={1}>
                <span>Senden</span>
                <CircularProgress size={20} />
              </Box>
            ) : (
              'Senden'
            )}
          </Button>
        </DialogActions>
      </SupportDialogLayout>
    </>
  );
};
